import { atom } from "jotai"
import api from "../services/api"
import Pusher from "pusher-js"
import {
  userAtom,
  roleAtom,
  planTokenAtom,
  loadingAtom,
  channelAtom,
  availableRolesAtom,
  lastPlanTokenAtom,
} from "./index"
import { PLAN_USER_ROLE } from "../constants/planUserRole"

// Plan state atom
export const planAtom = atom(null)

// Atom action for fetching a single plan
export const fetchPlanAtom = atom(
  null,
  async (get, set, token) => {
    if (!token) {
      set(planAtom, null)
      return
    }

    // Only show the full-page spinner when navigating to a new plan.
    // Background refreshes of the current plan run silently so that open
    // step modals are not unmounted and any in-progress mentor edits are preserved.
    const isNewPlan = get(planTokenAtom) !== token
    if (isNewPlan) {
      set(planAtom, null)
      set(loadingAtom, true)
    }
    set(planTokenAtom, token)

    try {
      // Initialize Pusher if not already done
      if (!get(channelAtom)) {
        const pusher = new Pusher(import.meta.env.VITE_PUSHER_APP_KEY, {
          cluster: import.meta.env.VITE_PUSHER_CLUSTER,
        })
        set(channelAtom, pusher.subscribe(import.meta.env.VITE_PUSHER_CHANNEL))
      }

      const response = await api.getPlan(token)
      set(planAtom, response.data)

      // Save this plan token as the last viewed plan
      set(lastPlanTokenAtom, token)

      // Determine user role
      const user = get(userAtom)

      // current_user_role is now an array of roles from the API
      const userRoles = response?.data?.current_user_role ?? [];

      // Grant admins all roles ONLY if they have no roles on this plan
      if (user?.is_admin && userRoles.length === 0) {
        const adminRoles = [
          PLAN_USER_ROLE.YOUTH,
          PLAN_USER_ROLE.MENTOR,
          PLAN_USER_ROLE.GUIDE,
        ];
        set(availableRolesAtom, adminRoles);
        const currentRole = get(roleAtom)
        set(roleAtom, adminRoles.includes(currentRole) ? currentRole : adminRoles[0]);
        return;
      }

      // Build available roles from API response (which now supports multiple roles per user)
      let roles = [...userRoles];

      // If no roles from API, fall back to checking user relationships
      if (roles.length === 0) {
        const mentors = (response?.data?.mentors ?? []).map(mentor => mentor.id);
        const guides = (response?.data?.guide ?? []).map(guide => guide.id);
        const youth = (response?.data?.youth ?? []).map(youth => youth.id);
        const initiators = (response?.data?.initiator ?? []).map(initiators => initiators.id);
        const guardians = (response?.data?.guardians ?? []).map(guardians => guardians.id);

        if (mentors.includes(user.id)) roles.push(PLAN_USER_ROLE.MENTOR);
        if (guides.includes(user.id)) roles.push(PLAN_USER_ROLE.GUIDE);
        if (youth.includes(user.id)) roles.push(PLAN_USER_ROLE.YOUTH);
        if (initiators.includes(user.id)) roles.push(PLAN_USER_ROLE.INITIATOR);
        if (guardians.includes(user.id)) roles.push(PLAN_USER_ROLE.GUARDIAN);
      }

      if (roles.length === 0) roles.push(PLAN_USER_ROLE.YOUTH);
      if (roles.includes(PLAN_USER_ROLE.GUIDE)) {
        if (!roles.includes(PLAN_USER_ROLE.YOUTH)) roles.push(PLAN_USER_ROLE.YOUTH);
        if (!roles.includes(PLAN_USER_ROLE.MENTOR)) roles.push(PLAN_USER_ROLE.MENTOR);
      }

      // Guardians can't do anything and technically don't get user accounts. However, guardians can still be added as mentors.
      // We need to filter out guardians from the available roles since they can't perform any actions, unless it is their only role.
      if (roles.length > 1 && roles.includes(PLAN_USER_ROLE.GUARDIAN)) {
        roles = roles.filter(role => role !== PLAN_USER_ROLE.GUARDIAN);
      }

      set(availableRolesAtom, roles);
      const currentRole = get(roleAtom)
      set(roleAtom, roles.includes(currentRole) ? currentRole : (roles[0] ?? PLAN_USER_ROLE.YOUTH));

    } catch (error) {
      console.error('Error fetching plan:', error)
    } finally {
      set(loadingAtom, false)
    }
  }
)
